"use client"

import type { Query, QueryClient } from "@tanstack/react-query"

type PersistScope = "global" | "user"

type PersistMeta = {
  persist?: PersistScope
}

type PersistedQueryEntry = {
  queryKey: unknown
  scope: PersistScope
  userId: string | null
  data: unknown
  persistedAt: number
}

type PersistedQueryState = {
  version: 1
  entries: Record<string, PersistedQueryEntry>
}

const STORAGE_KEY = "askjyotish.frontend.react-query-cache.v1"

function canUseStorage() {
  return typeof window !== "undefined"
}

function readPersistedState(): PersistedQueryState | null {
  if (!canUseStorage()) {
    return null
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as PersistedQueryState
    if (!parsed || parsed.version !== 1 || typeof parsed.entries !== "object") {
      return null
    }

    return parsed
  } catch {
    return null
  }
}

function writePersistedState(state: PersistedQueryState) {
  if (!canUseStorage()) {
    return
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore persistence failures.
  }
}

function queryStorageKey(query: Query): string {
  return JSON.stringify(query.queryKey)
}

function queryPersistMeta(query: Query): PersistMeta | null {
  const meta = query.meta as PersistMeta | undefined
  return meta && meta.persist ? meta : null
}

function clearUserScopedQueriesFromMemory(
  queryClient: QueryClient,
  currentUserId: string | null,
) {
  queryClient.removeQueries({
    predicate: (query) => {
      const meta = queryPersistMeta(query)
      if (!meta || meta.persist !== "user") {
        return false
      }

      const state = query.state.data as { userId?: unknown } | undefined
      if (currentUserId == null) {
        return true
      }

      return state?.userId !== currentUserId
    },
  })
}

function shouldPersistQuery(query: Query) {
  return queryPersistMeta(query) != null && query.state.status === "success"
}

export function clearPersistedQueryCache() {
  if (!canUseStorage()) {
    return
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore persistence failures.
  }
}

export function restorePersistedQueryCache(queryClient: QueryClient, currentUserId: string | null) {
  const persisted = readPersistedState()
  if (!persisted) {
    return
  }

  for (const entry of Object.values(persisted.entries)) {
    if (entry.scope === "user" && entry.userId !== currentUserId) {
      continue
    }

    queryClient.setQueryData(entry.queryKey as any, entry.data as any, {
      updatedAt: entry.persistedAt,
    })
  }
}

export function subscribeToPersistedQueryCache(
  queryClient: QueryClient,
  currentUserId: string | null,
) {
  if (!canUseStorage()) {
    return () => {}
  }

  clearUserScopedQueriesFromMemory(queryClient, currentUserId)
  restorePersistedQueryCache(queryClient, currentUserId)

  const cache = queryClient.getQueryCache()
  const unsubscribe = cache.subscribe((event: any) => {
    const query = event?.query as Query | undefined
    if (!query) {
      return
    }

    const meta = queryPersistMeta(query)
    if (!meta) {
      return
    }

    const state = readPersistedState() ?? { version: 1 as const, entries: {} }
    const key = queryStorageKey(query)

    if (!shouldPersistQuery(query)) {
      if (state.entries[key]) {
        delete state.entries[key]
        writePersistedState(state)
      }
      return
    }

    state.entries[key] = {
      queryKey: query.queryKey,
      scope: meta.persist ?? "global",
      userId: currentUserId,
      data: query.state.data,
      persistedAt: Date.now(),
    }
    writePersistedState(state)
  })

  return unsubscribe
}
